import { type ReactNode } from 'react';
import { cn } from 'utils/className';
import { type PlanTier } from '../consts';
import { RadioDot } from './RadioDot';

type PlanGroupCardProps = {
  tier: PlanTier;
  name: string;
  audience?: string;
  checked: boolean;
  collapsed?: boolean;
  onSelect: (tier: PlanTier) => void;
  children: ReactNode;
};

/**
 * A selectable plan tier (Team Pro / Enterprise). The header strip is the
 * accessible radio (`role="radio"`); selecting it drives the popup's tier state
 * and the footer CTA. The body slot holds tier-specific content.
 */
export const PlanGroupCard = ({
  tier,
  name,
  audience,
  checked,
  collapsed = false,
  onSelect,
  children,
}: PlanGroupCardProps) => {
  // Transparent so the card sits on the dialog's own #141414 surface, matching
  // the Figma (the tier cards have no fill — only an outline). `fill-bg-neutral`
  // resolves to #090A0B here, a too-dark box that doesn't match the design.
  const containerClassName = cn(
    'flex flex-col overflow-hidden rounded-lg border bg-transparent transition-colors font-inter',
    checked
      ? 'border-border-input-emphasis-active'
      : 'border-border-panel-neutral',
  );

  const headerClassName = cn(
    'flex cursor-pointer items-center justify-between px-6 py-3',
    (!collapsed || checked) && 'border-b',
    checked
      ? 'border-border-input-emphasis-active'
      : 'border-border-panel-neutral',
  );

  const isBodyVisible = !collapsed || checked;

  return (
    <div className={containerClassName}>
      <div
        role="radio"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => onSelect(tier)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(tier);
          }
        }}
        className={headerClassName}
      >
        <p className="text-sm text-text-panel-neutral-mid flex items-center gap-2">
          <span className="font-medium uppercase text-text-general-neutral-subtle">
            {name}
          </span>
          {audience && (
            <>
              <span className="inline-block size-1 rounded-full bg-purple-400" />
              <span className="text-text-general-neutral-subtle">
                {audience}
              </span>
            </>
          )}
        </p>
        <RadioDot checked={checked} />
      </div>

      {isBodyVisible && (
        <div className="flex flex-col gap-6 p-6">{children}</div>
      )}
    </div>
  );
};
